import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  MaterialBundle, MaterialDetail, MaterialKind, MaterialPayload, MaterialSources, MaterialStatus,
  MaterialUpdatePayload, MotherPlant, SeedTestPayload, SourcingRequest, SourcingRequestPayload,
} from '../types/nurseryMaterial';

/**
 * Propagation material, mother plants and sourcing requests
 * (docs/nursery-propagation-plan.md, Phase 1).
 */
class NurseryMaterialService {
  async list(filters: { kind?: MaterialKind; status?: MaterialStatus | 'all'; cultivar_id?: number } = {}):
    Promise<ApiResponse<MaterialBundle[]>> {
    const q = new URLSearchParams();
    if (filters.kind) q.set('kind', filters.kind);
    if (filters.status) q.set('status', filters.status);
    if (filters.cultivar_id) q.set('cultivar_id', String(filters.cultivar_id));
    const s = q.toString();
    return apiService.get(`/nursery/material${s ? `?${s}` : ''}`);
  }

  async get(id: number): Promise<ApiResponse<MaterialDetail>> {
    return apiService.get(`/nursery/material/${id}`);
  }

  async create(data: MaterialPayload): Promise<ApiResponse<MaterialDetail>> {
    return apiService.post('/nursery/material', data);
  }

  async update(id: number, data: MaterialUpdatePayload): Promise<ApiResponse<MaterialDetail>> {
    return apiService.put(`/nursery/material/${id}`, data);
  }

  async adjust(id: number, remaining: number, reason: string): Promise<ApiResponse<MaterialDetail>> {
    return apiService.post(`/nursery/material/${id}/adjust`, { remaining, reason });
  }

  async writeOff(id: number, reason: string): Promise<ApiResponse<MaterialDetail>> {
    return apiService.post(`/nursery/material/${id}/write-off`, { reason });
  }

  async addTest(id: number, data: SeedTestPayload): Promise<ApiResponse<MaterialDetail>> {
    return apiService.post(`/nursery/material/${id}/tests`, data);
  }

  async sources(cultivarId: number): Promise<ApiResponse<MaterialSources>> {
    return apiService.get(`/nursery/material/sources?cultivar_id=${cultivarId}`);
  }

  /** A4 sheet of labels, each with MAT:<id>. */
  async labelsPdf(ids: number[]): Promise<Blob> {
    return apiService.get(`/nursery/material/labels.pdf?ids=${ids.join(',')}`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  async mothers(): Promise<ApiResponse<MotherPlant[]>> {
    return apiService.get('/nursery/mothers');
  }

  async setWoodSource(cultivarAreaId: number, isWoodSource: boolean):
    Promise<ApiResponse<{ id: number; is_wood_source: boolean }>> {
    return apiService.put(`/nursery/mothers/plantings/${cultivarAreaId}`, { is_wood_source: isWoodSource });
  }

  async requests(status?: 'all' | 'closed' | 'open' | 'sourced'): Promise<ApiResponse<SourcingRequest[]>> {
    return apiService.get(`/nursery/sourcing-requests${status ? `?status=${status}` : ''}`);
  }

  async createRequest(data: SourcingRequestPayload): Promise<ApiResponse<SourcingRequest>> {
    return apiService.post('/nursery/sourcing-requests', data);
  }

  async markSourced(id: number, sourceNote: string): Promise<ApiResponse<SourcingRequest>> {
    return apiService.post(`/nursery/sourcing-requests/${id}/sourced`, { source_note: sourceNote });
  }

  async cancelRequest(id: number, reason: string): Promise<ApiResponse<SourcingRequest>> {
    return apiService.post(`/nursery/sourcing-requests/${id}/cancel`, { reason });
  }
}

export const nurseryMaterialService = new NurseryMaterialService();
export default nurseryMaterialService;
